import { getNotificationDeviceType } from './platform'

const DEVICE_ID_STORAGE_KEY = 'atlas.pushDeviceId'

/**
 * A stable id for this browser install, sent with every push subscription.
 *
 * The server needs to know when a new endpoint replaces one it already holds for
 * the same install, and nothing on the subscription itself can tell it: the
 * endpoint is the thing that changed — iOS mints a new one on every reinstall or
 * rotation — and the user agent moves with the browser version while being shared
 * by every machine on that build. Without a pairing key the old row lived on and
 * the phone kept getting a banner per orphan.
 *
 * localStorage rather than sessionStorage or a cookie: it has to outlive the tab
 * that wrote it and the subscription it was minted alongside, and it must never
 * travel on an ordinary request. It is a random opaque id — no device
 * fingerprinting, nothing derived from the machine.
 *
 * Undefined when storage is unavailable (private mode, partitioned contexts, a
 * browser set to block site data). That degrades cleanly: the server keeps the
 * subscription and prunes it under its longer abandoned-row window instead.
 */
export const getPushDeviceId = (): string | undefined => {
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY)
    if (existing) return existing

    // randomUUID needs a secure context. Atlas is always https, but a browser
    // without it should lose the optimisation, not the subscription.
    const deviceId = crypto?.randomUUID?.()
    if (!deviceId) return undefined

    localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId)
    return deviceId
  } catch {
    return undefined
  }
}

/** What the server stores for a push subscription registered from this browser. */
export const buildPushSubscriptionPayload = (subscription: PushSubscription) => {
  const { keys } = subscription.toJSON()
  return {
    endpoint: subscription.endpoint,
    keys: { p256dh: keys?.p256dh || '', auth: keys?.auth || '' },
    userAgent: navigator.userAgent,
    deviceId: getPushDeviceId(),
    deviceType: getNotificationDeviceType(),
  }
}
